/** Helpers for PromQL builder label pickers (group-by vs infra noise). */

const INFRA_LABEL_PREFIXES = [
  "beta_kubernetes_io_",
  "failure_domain_",
  "topology_kubernetes_io_",
  "node_kubernetes_io_",
  "cloud_google_com_",
  "eks_amazonaws_com_",
];

export function isInfraNoiseLabel(key) {
  const normalized = String(key || "").toLowerCase();
  if (!normalized || normalized === "__name__") return true;
  if (normalized === "account_id" || normalized === "cluster" || normalized === "region") {
    return true;
  }
  return INFRA_LABEL_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

export function seriesMatchesLabelFilters(series, filters = []) {
  if (!series || typeof series !== "object") return false;

  const active = (filters || []).filter((f) => f?.key && f?.value !== undefined && f?.value !== "");
  if (!active.length) return true;

  return active.every((filter) => {
    const actual = series[filter.key];
    if (actual === undefined || actual === null) return false;

    const expected = String(filter.value);
    const actualStr = String(actual);
    const op = filter.op || "=";

    switch (op) {
      case "!=":
        return actualStr !== expected;
      case "=~":
        try {
          return new RegExp(expected).test(actualStr);
        } catch {
          return false;
        }
      case "!~":
        try {
          return !new RegExp(expected).test(actualStr);
        } catch {
          return false;
        }
      case "=":
      default:
        return actualStr === expected;
    }
  });
}

function collectLabelsFromSeries(seriesList = []) {
  const keys = new Set();
  (seriesList || []).forEach((series) => {
    if (!series || typeof series !== "object") return;
    Object.keys(series).forEach((key) => {
      if (key !== "__name__") keys.add(key);
    });
  });
  return keys;
}

/**
 * Build sum/avg "by" label options from series that match current label filters.
 * Active filter keys are always listed first; infra noise labels are omitted unless selected.
 */
export function buildGroupingLabelKeys({
  metricSeries = [],
  labelFilters = [],
  ops = [],
  fallbackLabelKeys = [],
} = {}) {
  const filterKeyOrder = [];
  const preserve = new Set();

  (labelFilters || []).forEach((filter) => {
    if (filter?.key) {
      preserve.add(filter.key);
      if (!filterKeyOrder.includes(filter.key)) {
        filterKeyOrder.push(filter.key);
      }
    }
  });

  (ops || []).forEach((op) => {
    (op?.params?.by || []).forEach((key) => preserve.add(key));
  });

  const activeFilters = (labelFilters || []).filter(
    (f) => f?.key && f?.value !== undefined && f?.value !== "",
  );
  const filteredSeries = activeFilters.length
    ? metricSeries.filter((series) => seriesMatchesLabelFilters(series, activeFilters))
    : metricSeries;

  const seriesForLabels =
    filteredSeries.length > 0 ? filteredSeries : metricSeries;

  const labelSet = collectLabelsFromSeries(seriesForLabels);
  (fallbackLabelKeys || []).forEach((key) => {
    if (key) labelSet.add(key);
  });
  preserve.forEach((key) => labelSet.add(key));

  const ordered = [];
  filterKeyOrder.forEach((key) => {
    if (labelSet.has(key)) ordered.push(key);
  });

  const rest = [...labelSet]
    .filter((key) => !ordered.includes(key))
    .filter((key) => preserve.has(key) || !isInfraNoiseLabel(key))
    .sort((a, b) => String(a).localeCompare(String(b)));

  return [...ordered, ...rest];
}
